import { createFileRoute, Link } from '@tanstack/react-router'
import { Button } from '~/components/ui/button'

export const Route = createFileRoute('/')({
  head: () => ({
    meta: [
      {
        title:
          'Provenance — Digital Product Passports for Fine Watches | WatchCentral',
      },
      {
        name: 'description',
        content:
          'Provenance by WatchCentral creates tamper-evident digital passports for fine watches. Track ownership history, service records, and chain of custody — free for collectors, available on iOS and Android.',
      },
      { property: 'og:type', content: 'website' },
      {
        property: 'og:url',
        content: 'https://provenance.watchcentral.com',
      },
      {
        property: 'og:title',
        content: 'Provenance — Digital Product Passports for Fine Watches',
      },
      {
        property: 'og:description',
        content:
          'Tamper-evident digital passports for fine watches. Ownership history, service records, and chain of custody — cryptographically verified.',
      },
      {
        property: 'og:image',
        content: 'https://provenance.watchcentral.com/og-image.png',
      },
      { name: 'twitter:card', content: 'summary_large_image' },
      {
        name: 'twitter:title',
        content: 'Provenance — Digital Passports for Fine Watches',
      },
      {
        name: 'twitter:description',
        content:
          'Tamper-evident digital passports for fine watches by WatchCentral. Free for collectors.',
      },
      {
        name: 'twitter:image',
        content: 'https://provenance.watchcentral.com/og-image.png',
      },
      {
        name: 'keywords',
        content:
          'digital product passport, watch provenance, fine watch authentication, watch ownership history, blockchain watch records, DPP, WatchCentral',
      },
    ],
    links: [
      { rel: 'canonical', href: 'https://provenance.watchcentral.com' },
    ],
  }),
  component: HomePage,
})


interface FeatureCard {
  num: string
  label: string
  title: string
  description: string
  glyph: React.ReactNode
  readout: Array<{ k: string; v: string }>
}


const FEATURES: FeatureCard[] = [
  {
    num: '01',
    label: 'DPP',
    title: 'Digital Product Passport',
    description:
      'Brand, model, reference, serial, issuance date and provenance history — preserved on a tamper-evident ledger.',
    glyph: <WatchGlyph />,
    readout: [
      { k: 'S/N', v: '126610LN' },
      { k: 'ISSUED', v: '2024.06.17' },
    ],
  },
  {
    num: '02',
    label: 'TIME',
    title: 'Acoustic Timegrapher',
    description:
      'Measure rate, amplitude and beat-error from the microphone. Trend movement performance over years.',
    glyph: <TimegrapherGlyph />,
    readout: [
      { k: 'RATE', v: '+2 s/d' },
      { k: 'AMP', v: '278°' },
    ],
  },
  {
    num: '03',
    label: 'INTAKE',
    title: 'Operations Intake',
    description:
      'Service requests, provenance inquiries, transfers and warranty estimates — routed directly to operations.',
    glyph: <IntakeGlyph />,
    readout: [
      { k: 'SLA', v: '24h' },
      { k: 'OPS', v: 'NYC' },
    ],
  },
  {
    num: '04',
    label: 'CUSTODY',
    title: 'Chain of Custody',
    description:
      'Every transfer signed. Every service logged. A complete, append-only history that travels with the watch.',
    glyph: <CustodyGlyph />,
    readout: [
      { k: 'BLOCKS', v: '3' },
      { k: 'STATUS', v: 'VERIFIED' },
    ],
  },
]

const HOW_STEPS = [
  {
    n: '01',
    tag: 'Add',
    title: 'Identify the watch',
    body: 'Photograph the dial or enter reference manually. The system suggests the model, calibre and reference for confirmation.',
    meta: 'Instant · 4s',
  },
  {
    n: '02',
    tag: 'Issue',
    title: 'Mint the passport',
    body: 'WatchCentral signs and issues the DPP — specification, photographs and ownership are written to the ledger.',
    meta: 'Ledger · ETH/L2',
  },
  {
    n: '03',
    tag: 'Operate',
    title: 'Service & measure',
    body: 'Run the acoustic Time Grapher, request authorised service and append warranty & appraisal records.',
    meta: 'Beta · Audio',
  },
  {
    n: '04',
    tag: 'Transfer',
    title: 'Hand it on',
    body: 'Reassign passport ownership to a new holder. The history travels with the watch — not the previous owner.',
    meta: '2–7 days',
  },
]

const WATCH_MARKS = [
  { name: 'Rolex', sub: 'Datejust · Submariner' },
  { name: 'Patek Philippe', sub: 'Aquanaut · Calatrava' },
  { name: 'Omega', sub: 'Speedmaster · Seamaster' },
  { name: 'Audemars Piguet', sub: 'Royal Oak' },
  { name: 'Vacheron', sub: 'Overseas · Patrimony' },
  { name: 'A. Lange & Söhne', sub: 'Lange 1 · Saxonia' },
  { name: 'Cartier', sub: 'Tank · Santos' },
  { name: 'Jaeger-LeCoultre', sub: 'Reverso' },
  { name: '+ 124 more', sub: 'Coverage growing' },
]


function WatchGlyph() {
  return <div className="watch-glyph" aria-hidden="true" />
}

function TimegrapherGlyph() {
  return (
    <svg
      width="48"
      height="48"
      viewBox="0 0 48 48"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M2 24 L8 24 L10 14 L14 34 L18 18 L22 30 L26 22 L30 26 L34 20 L38 28 L42 24 L46 24"
        stroke="#D97868"
        strokeWidth="1"
        fill="none"
      />
      <line
        x1="2"
        y1="24"
        x2="46"
        y2="24"
        stroke="var(--line-strong)"
        strokeWidth="0.5"
        strokeDasharray="2 2"
      />
    </svg>
  )
}

function IntakeGlyph() {
  return (
    <svg
      width="48"
      height="48"
      viewBox="0 0 48 48"
      fill="none"
      aria-hidden="true"
    >
      <rect
        x="4"
        y="8"
        width="40"
        height="6"
        fill="none"
        stroke="#6B7290"
        strokeWidth="0.75"
      />
      <rect
        x="4"
        y="21"
        width="30"
        height="6"
        fill="none"
        stroke="#6B7290"
        strokeWidth="0.75"
      />
      <rect
        x="4"
        y="34"
        width="20"
        height="6"
        fill="none"
        stroke="#D97868"
        strokeWidth="0.75"
      />
    </svg>
  )
}

function CustodyGlyph() {
  return (
    <svg
      width="48"
      height="48"
      viewBox="0 0 48 48"
      fill="none"
      aria-hidden="true"
    >
      <rect
        x="6"
        y="6"
        width="10"
        height="10"
        fill="none"
        stroke="#A8AEC6"
        strokeWidth="0.75"
      />
      <rect
        x="19"
        y="19"
        width="10"
        height="10"
        fill="none"
        stroke="#A8AEC6"
        strokeWidth="0.75"
      />
      <rect
        x="32"
        y="32"
        width="10"
        height="10"
        fill="none"
        stroke="#D97868"
        strokeWidth="0.75"
      />
      <line
        x1="11"
        y1="16"
        x2="24"
        y2="19"
        stroke="var(--line-strong)"
        strokeWidth="0.5"
      />
      <line
        x1="24"
        y1="29"
        x2="37"
        y2="32"
        stroke="var(--line-strong)"
        strokeWidth="0.5"
      />
    </svg>
  )
}


function Nav() {
  return (
    <nav
      className="sticky top-0 z-50 border-b border-[var(--line)] bg-[rgba(17,19,34,0.82)] backdrop-blur-[14px]"
      aria-label="Main navigation"
    >
      <div className="mx-auto flex h-16 max-w-[1320px] items-center justify-between px-[var(--gutter)]">
        {}
        <Link
          to="/"
          className="flex items-center gap-[10px] font-mono text-[13px] font-[600] tracking-[0.28em] uppercase text-ink"
          aria-label="Provenance — Home"
        >
          <span className="brand-mark" aria-hidden="true" />
          Provenance
          <span className="ml-1 border-l border-[var(--line-strong)] pl-[10px] font-mono text-[9px] font-[400] tracking-[0.2em] text-ink-mute">
            by WatchCentral
          </span>
        </Link>

        {}
        <div className="hidden items-center gap-7 md:flex" role="list">
          <a
            href="#product"
            className="font-mono text-[11px] font-[500] tracking-[0.14em] uppercase text-ink-soft transition-colors hover:text-ink"
            role="listitem"
          >
            Product
          </a>
          <a
            href="/download"
            className="font-mono text-[11px] font-[500] tracking-[0.14em] uppercase text-ink-soft transition-colors hover:text-ink"
            role="listitem"
          >
            Download
          </a>
        </div>

        {}
        <div className="flex items-center gap-3">
          <a
            href="/login"
            className="hidden font-mono text-[11px] font-[500] tracking-[0.12em] uppercase border border-[var(--line-strong)] px-[14px] py-[10px] text-ink transition-colors hover:border-ink-soft sm:inline-flex"
          >
            Sign in
          </a>
          <a
            href="/download"
            className="inline-flex items-center gap-[10px] bg-accent border border-accent px-[14px] py-[10px] font-mono text-[11px] font-[600] tracking-[0.12em] uppercase text-accent-ink transition-colors hover:bg-accent-dark hover:border-accent-dark"
          >
            Get the app
          </a>
        </div>
      </div>
    </nav>
  )
}


function DeviceMockup() {
  const watches = [
    { name: 'Rolex · Datejust', ref: 'elantestelan', circle: false },
    { name: 'Rolex · Day Date', ref: '9800740-test-mar30', circle: true },
    { name: 'Omega · Speedmaster Pro', ref: '310.30.42.50.01.001', circle: false },
    { name: 'Patek Philippe · Aquanaut', ref: '5167/1A-001', circle: true },
    { name: 'AP · Royal Oak Jumbo', ref: '15202ST.OO.1240ST.01', circle: false },
  ]

  return (
    <div className="relative mx-auto" style={{ width: 300 }}>
      {}
      <div
        className="bracket-frame relative border border-[var(--line-strong)] bg-bg-2 mx-auto"
        style={{ width: 300, aspectRatio: '300 / 620', padding: '16px 14px 22px' }}
        role="img"
        aria-label="Provenance app mockup showing a list of digital watch passports"
      >
        <span className="bk-bl" aria-hidden="true" />
        <span className="bk-br" aria-hidden="true" />

        {}
        <div
          className="absolute left-1/2 top-[5px] -translate-x-1/2 bg-surface-3"
          style={{ width: 72, height: 4 }}
          aria-hidden="true"
        />

        {}
        <div
          className="relative h-full overflow-hidden border border-[var(--line)] bg-bg"
          style={{ padding: '32px 16px 16px' }}
        >
          {}
          <div
            className="absolute left-4 right-4 top-2 flex items-center justify-between font-mono text-[9px] tracking-[0.14em] text-ink-mute"
            aria-hidden="true"
          >
            <span>19:22</span>
            <span>● ● ● 5G</span>
          </div>

          {}
          <div className="mb-3" aria-hidden="true">
            <div className="font-mono text-[8px] tracking-[0.22em] uppercase text-ink-mute mb-1">
              PROVENANCE
            </div>
            <div className="font-mono text-[18px] font-[500] leading-[1.1] text-ink">
              Digital Product<br />Passports
            </div>
          </div>

          {}
          <ul className="divide-y divide-[var(--line)]" aria-hidden="true">
            {watches.map((w, i) => (
              <li key={i} className="flex items-center gap-[9px] py-[9px]">
                <div
                  className={
                    w.circle
                      ? 'relative shrink-0 rounded-full border border-ink-soft'
                      : 'shrink-0 border border-[var(--line)] bg-surface-2'
                  }
                  style={{
                    width: 34,
                    height: 34,
                    background: w.circle
                      ? undefined
                      : 'repeating-linear-gradient(45deg,transparent 0 4px,rgba(239,241,245,0.04) 4px 5px), #161930',
                  }}
                />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-[11px] font-[500] text-ink">
                    {w.name}
                  </div>
                  <div className="font-mono text-[7.5px] tracking-[0.14em] uppercase text-ink-mute mt-[2px]">
                    {w.ref}
                  </div>
                </div>
                <span className="text-[11px] text-ink-mute">↗</span>
              </li>
            ))}
          </ul>

          {}
          <div
            className="absolute bottom-3 left-4 right-4 grid grid-cols-4 border-t border-[var(--line)] pt-[10px]"
            aria-hidden="true"
          >
            {['Blockchain', 'Intake', 'Time', 'Account'].map((tab, i) => (
              <div
                key={tab}
                className={`flex flex-col items-center gap-1 font-mono text-[7px] tracking-[0.14em] uppercase ${i === 0 ? 'text-accent' : 'text-ink-mute'}`}
              >
                <div
                  className={`h-[11px] w-[11px] border ${i === 0 ? 'border-accent bg-[var(--accent-soft)]' : 'border-current'}`}
                />
                {tab}
              </div>
            ))}
          </div>
        </div>
      </div>

      {}
      <p className="mt-7 text-center font-mono text-[10px] tracking-[0.18em] uppercase text-ink-mute">
        FIG. 01 — APP / iOS · ANDROID
      </p>
    </div>
  )
}

function HeroSection() {
  return (
    <header className="relative overflow-hidden py-14 pb-22" style={{ paddingBottom: '88px' }}>
      {}
      <svg
        className="pointer-events-none absolute right-[-10%] top-1/2 -translate-y-1/2 opacity-25"
        style={{ width: 720, height: 720 }}
        viewBox="0 0 720 720"
        aria-hidden="true"
      >
        {[360, 300, 240, 180, 120, 60].map((r) => (
          <circle key={r} cx="360" cy="360" r={r} fill="none" stroke="#A8AEC6" strokeWidth="0.5" />
        ))}
        <g stroke="#D97868" strokeWidth="1" opacity="0.5">
          <line x1="360" y1="0" x2="360" y2="720" />
          <line x1="0" y1="360" x2="720" y2="360" />
        </g>
      </svg>

      <div className="relative z-10 mx-auto max-w-[1320px] px-[var(--gutter)]">
        <div className="grid items-center gap-16 lg:grid-cols-[1.15fr_0.85fr]">
          {}
          <div>
            {}
            <div
              className="mb-7 flex items-center gap-4 font-mono text-[11px] tracking-[0.16em] uppercase text-ink-mute"
              aria-hidden="true"
            >
              <span className="flex items-center gap-2">
                <span className="inline-block h-1 w-1 bg-accent" />
                EST. 2024 · NEW YORK
              </span>
              <span className="opacity-40">|</span>
              <span>PHIGORA · WATCH CENTRAL</span>
            </div>

            {}
            <h1
              className="font-mono font-[500] leading-[0.98] tracking-[-0.02em] text-ink"
              style={{ fontSize: 'clamp(40px, 6.5vw, 88px)' }}
            >
              A digital passport
              <br />
              for every watch.
            </h1>

            {}
            <p
              className="mt-7 text-ink-soft"
              style={{ fontSize: 'clamp(16px, 1.4vw, 19px)', lineHeight: 1.55, maxWidth: '52ch' }}
            >
              Provenance is the issuance and audit platform for fine-watch
              ownership. Each piece carries a tamper-evident record of its
              specification, service history and chain of custody — verifiable
              in seconds, transferable for life.
            </p>

            {}
            <div className="mt-9 flex flex-wrap items-center gap-3">
              <a
                href="/download"
                className="inline-flex items-center gap-[10px] bg-accent border border-accent px-7 py-[18px] font-mono text-[13px] font-[600] tracking-[0.14em] uppercase text-accent-ink transition-colors hover:bg-accent-dark hover:border-accent-dark"
              >
                Get the app
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                  <path d="M3 7h8M7 3l4 4-4 4" />
                </svg>
              </a>
              <a
                href="#how"
                className="inline-flex items-center gap-[10px] border border-[var(--line-strong)] px-7 py-[18px] font-mono text-[13px] font-[500] tracking-[0.14em] uppercase text-ink transition-colors hover:border-ink-soft"
              >
                See how it works
              </a>
            </div>

            {}
            <dl
              className="mt-14 grid grid-cols-3 gap-8 border-t border-[var(--line)] pt-7"
              aria-label="Key metrics"
            >
              <div className="hero-stat">
                <dt className="font-mono text-[10px] tracking-[0.14em] uppercase text-ink-mute mt-2 order-2">
                  Passports issued
                </dt>
                <dd
                  className="font-mono font-[500] leading-[1] tracking-[-0.01em] text-ink order-1"
                  style={{ fontSize: 32 }}
                >
                  12,408
                </dd>
              </div>
              <div className="flex flex-col gap-2">
                <dd
                  className="font-mono font-[500] leading-[1] tracking-[-0.01em] text-ink"
                  style={{ fontSize: 32 }}
                >
                  38
                </dd>
                <dt className="font-mono text-[10px] tracking-[0.14em] uppercase text-ink-mute">
                  Authorised dealers
                </dt>
              </div>
              <div className="flex flex-col gap-2">
                <dd
                  className="font-mono font-[500] leading-[1] tracking-[-0.01em] text-ink"
                  style={{ fontSize: 32 }}
                >
                  99.97
                  <span className="text-ink-mute" style={{ fontSize: '0.6em' }}>%</span>
                </dd>
                <dt className="font-mono text-[10px] tracking-[0.14em] uppercase text-ink-mute">
                  Audit integrity
                </dt>
              </div>
            </dl>
          </div>

          {}
          <div className="flex justify-center mt-8 lg:mt-0">
            <DeviceMockup />
          </div>
        </div>
      </div>
    </header>
  )
}


function SectionHeader({
  no,
  total = '05',
  label,
  title,
  fig,
}: {
  no: string
  total?: string
  label: string
  title: string
  fig: string
}) {
  return (
    <div className="mb-12 grid grid-cols-[auto_1fr_auto] items-end gap-6 border-b border-[var(--line)] pb-6">
      <div className="font-mono text-[11px] tracking-[0.16em] uppercase text-ink-mute">
        <b className="font-[500] text-accent">{no}</b> / {total} — {label}
      </div>
      <h2
        className="font-mono font-[500] leading-[1.04] tracking-[-0.015em] text-ink m-0"
        style={{ fontSize: 'clamp(28px, 3.6vw, 48px)', maxWidth: '22ch' }}
      >
        {title}
      </h2>
      <div className="text-right font-mono text-[11px] tracking-[0.16em] uppercase text-ink-mute">
        {fig}
      </div>
    </div>
  )
}


function FeaturesSection() {
  return (
    <section
      className="relative py-[clamp(64px,9vw,128px)]"
      id="product"
      aria-labelledby="features-heading"
    >
      <div className="relative z-10 mx-auto max-w-[1320px] px-[var(--gutter)]">
        <SectionHeader
          no="01"
          label="PRODUCT"
          title="Built like the watches it documents."
          fig="FIG. A — MODULES"
        />

        <div className="grid gap-6 sm:grid-cols-2 xl:grid-cols-4">
          {FEATURES.map((f) => (
            <article
              key={f.num}
              className="bracket-frame relative flex min-h-[280px] flex-col gap-4 border border-[var(--line)] bg-surface p-7"
              aria-label={f.title}
            >
              <span className="bk-bl" aria-hidden="true" />
              <span className="bk-br" aria-hidden="true" />

              <div className="font-mono text-[11px] tracking-[0.16em] text-accent">
                {f.num} · {f.label}
              </div>
              <h3 className="font-mono text-[22px] font-[500] leading-[1.1] m-0 text-ink">
                {f.title}
              </h3>
              <p className="text-[14px] leading-[1.6] text-ink-soft m-0">
                {f.description}
              </p>

              {}
              <div className="mt-auto flex items-center gap-3 border-t border-[var(--line)] pt-4" aria-hidden="true">
                {f.glyph}
                <div className="font-mono text-[11px] tracking-[0.1em] text-ink-mute">
                  {f.readout.map((r) => (
                    <div key={r.k}>
                      {r.k} · <span className="text-ink">{r.v}</span>
                    </div>
                  ))}
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  )
}


function HowSection() {
  return (
    <section
      className="relative py-[clamp(64px,9vw,128px)]"
      id="how"
      aria-labelledby="how-heading"
    >
      <div className="relative z-10 mx-auto max-w-[1320px] px-[var(--gutter)]">
        <SectionHeader
          no="02"
          label="HOW"
          title="Four steps, signed at every hand-off."
          fig="FIG. B — FLOW"
        />

        <div
          className="grid border border-[var(--line)]"
          style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}
          role="list"
          aria-label="How Provenance works"
        >
          {HOW_STEPS.map((s, i) => (
            <div
              key={s.n}
              className={`bg-surface p-8 ${i < HOW_STEPS.length - 1 ? 'border-r border-[var(--line)]' : ''}`}
              role="listitem"
            >
              <div
                className="font-mono font-[300] leading-[1] text-accent"
                style={{ fontSize: 56 }}
                aria-hidden="true"
              >
                {s.n}
              </div>
              <div className="mt-1 font-mono text-[9px] tracking-[0.16em] uppercase text-ink-mute">
                {s.tag}
              </div>
              <h3 className="mt-6 mb-3 font-mono text-[18px] font-[500] text-ink">
                {s.title}
              </h3>
              <p className="text-[13px] leading-[1.55] text-ink-soft m-0 mb-4">
                {s.body}
              </p>
              <div className="font-mono text-[9px] tracking-[0.16em] uppercase text-ink-mute">
                {s.meta}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}


function IndustrySection() {
  return (
    <section
      className="relative py-[clamp(64px,9vw,128px)]"
      id="brands"
      aria-labelledby="industry-heading"
    >
      <div className="relative z-10 mx-auto max-w-[1320px] px-[var(--gutter)]">
        <SectionHeader
          no="03"
          label="INDUSTRY"
          title="Trusted at the bench, at the counter, at the safe."
          fig="FIG. C — PARTNERS"
        />

        <div className="grid gap-14 lg:grid-cols-2">
          {}
          <div>
            <p
              className="text-ink-soft mb-6"
              style={{ fontSize: 'clamp(16px, 1.4vw, 19px)', lineHeight: 1.55, maxWidth: '52ch' }}
            >
              Provenance is built for the people who actually work on watches —
              independent watchmakers, authorised dealers, auction houses and
              private collections. The mobile app handles the public face; the
              operator console handles the rest.
            </p>

            <dl className="mb-8 grid gap-3" style={{ gridTemplateColumns: '140px 1fr' }}>
              {[
                {
                  k: 'Watchmakers',
                  v: 'Append service records as you complete them. Photographs and timegrapher runs attached.',
                },
                {
                  k: 'Dealers',
                  v: 'Verify a piece in seconds. Transfer passports at point of sale with operator review.',
                },
                {
                  k: 'Auction',
                  v: 'Catalogue provenance with cryptographic certainty. Export bidder-ready proofs.',
                },
                {
                  k: 'Collectors',
                  v: 'One library, one history, transferable for life. No expired warranty cards in shoeboxes.',
                },
              ].map(({ k, v }) => (
                <>
                  <dt
                    key={`k-${k}`}
                    className="font-mono text-[11px] tracking-[0.14em] uppercase text-ink-mute font-[500] m-0 self-baseline"
                  >
                    {k}
                  </dt>
                  <dd key={`v-${k}`} className="text-[14px] text-ink m-0">
                    {v}
                  </dd>
                </>
              ))}
            </dl>

            <a
              href="mailto:sales@watchcentral.com"
              className="inline-flex items-center gap-2 border-b border-transparent pb-[2px] font-mono text-[11px] font-[500] tracking-[0.14em] uppercase text-accent transition-colors hover:border-accent"
            >
              Contact sales · sales@watchcentral.com
              <svg viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5" className="h-3 w-3" aria-hidden="true">
                <path d="M3 7h8M7 3l4 4-4 4" />
              </svg>
            </a>
          </div>

          {}
          <div>
            <div
              className="grid border border-[var(--line)]"
              style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}
              aria-label="Supported watch brands"
            >
              {WATCH_MARKS.map((m, i) => (
                <div
                  key={m.name}
                  className="relative flex flex-col items-center justify-center bg-surface font-mono text-[11px] tracking-[0.18em] uppercase text-ink-soft"
                  style={{
                    aspectRatio: '1.6 / 1',
                    borderRight: (i + 1) % 3 !== 0 ? '1px solid var(--line)' : undefined,
                    borderBottom: i < WATCH_MARKS.length - 3 ? '1px solid var(--line)' : undefined,
                    fontSize: 11,
                  }}
                >
                  {m.name}
                  <span className="absolute bottom-2 font-mono text-[8px] tracking-[0.14em] text-ink-mute">
                    {m.sub}
                  </span>
                </div>
              ))}
            </div>
            <p className="mt-4 text-right font-mono text-[10px] tracking-[0.14em] uppercase text-ink-mute">
              All trademarks property of respective owners
            </p>
          </div>
        </div>
      </div>
    </section>
  )
}


function AppleIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M16.4 12.5c0-2.3 1.9-3.4 2-3.4-1.1-1.6-2.8-1.8-3.4-1.8-1.4-.1-2.8.9-3.5.9-.7 0-1.9-.8-3.1-.8-1.6 0-3 .9-3.8 2.4-1.6 2.8-.4 6.9 1.2 9.2.8 1.1 1.7 2.3 2.9 2.3 1.2 0 1.6-.7 3-.7 1.4 0 1.8.7 3 .7 1.2 0 2-1.1 2.8-2.2.9-1.3 1.2-2.5 1.3-2.6-.1 0-2.4-1-2.4-3zM14 4.4c.6-.8 1.1-1.9.9-3-1 .1-2.1.7-2.8 1.5-.6.7-1.1 1.8-1 2.9 1.1.1 2.2-.6 2.9-1.4z" />
    </svg>
  )
}

function GooglePlayIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M3.6 1.8c-.3.3-.5.8-.5 1.4v17.5c0 .6.2 1.1.5 1.4l.1.1L13.4 12 3.7 1.7l-.1.1zM17 8.4l-3.6 3.6L17 15.6l4.4-2.5c1.2-.7 1.2-1.8 0-2.5L17 8.4zm-1.1-.6L4.7 1.4c-.4-.2-.8-.3-1.1-.1l11 11 1.3-3.5zM4.7 22.6l11.2-6.4-1.3-1.3L3.6 22.7c.3.2.7.1 1.1-.1z" />
    </svg>
  )
}

function QrCode() {
  return (
    <div
      className="relative border border-[var(--line-strong)] bg-bg p-4"
      aria-label="QR code to download Provenance app"
    >
      {}
      <div
        className="h-[120px] w-[120px] border-[8px] border-ink"
        style={{
          background: `
            conic-gradient(from 0deg, #EFF1F5 0 25%, transparent 0 50%, #EFF1F5 0 75%, transparent 0),
            repeating-conic-gradient(#EFF1F5 0 8deg, transparent 8deg 16deg)
          `,
          backgroundBlendMode: 'multiply',
          position: 'relative',
        }}
        role="img"
      >
        <div
          className="absolute inset-[30%] bg-ink"
        />
      </div>
    </div>
  )
}

function DownloadSection() {
  return (
    <section
      className="relative py-[clamp(64px,9vw,128px)]"
      id="download"
      aria-labelledby="download-heading"
    >
      <div className="relative z-10 mx-auto max-w-[1320px] px-[var(--gutter)]">
        <SectionHeader
          no="04"
          total="04"
          label="INSTALL"
          title="Carry it on the wrist that wears the watch."
          fig="FIG. D — DOWNLOAD"
        />

        {}
        <div
          className="bracket-frame relative grid items-center gap-12 border border-[var(--line)] p-16 lg:grid-cols-[1.2fr_1fr]"
          style={{
            background: 'linear-gradient(180deg, rgba(217,120,104,0.06), transparent 60%), #161930',
          }}
          aria-labelledby="download-heading"
        >
          <span className="bk-bl" aria-hidden="true" />
          <span className="bk-br" aria-hidden="true" />

          <div>
            <div className="mb-4 font-mono text-[11px] tracking-[0.16em] uppercase text-accent">
              PROVENANCE FOR MOBILE
            </div>
            <h2
              id="download-heading"
              className="font-mono font-[500] leading-[1.04] tracking-[-0.015em] text-ink mb-4"
              style={{ fontSize: 'clamp(28px, 3.4vw, 44px)' }}
            >
              Free for owners. Always.
            </h2>
            <p
              className="text-ink-soft mb-6"
              style={{ maxWidth: '48ch', lineHeight: 1.55, fontSize: 'clamp(15px,1.2vw,18px)' }}
            >
              The Provenance mobile app is free to download. There is no
              subscription for holding or viewing your DPPs — pricing only
              applies to operations and dealer-side tooling.
            </p>

            {}
            <div className="flex flex-wrap gap-3">
              <a
                href="/download"
                className="inline-flex min-w-[180px] items-center gap-3 border border-[var(--line-strong)] bg-surface px-5 py-[14px] text-ink transition-colors hover:border-accent"
                aria-label="Download Provenance on the App Store"
              >
                <AppleIcon />
                <div>
                  <div className="font-mono text-[9px] tracking-[0.14em] uppercase text-ink-mute">
                    Download on the
                  </div>
                  <div className="font-mono text-[16px] font-[500] text-ink">
                    App Store
                  </div>
                </div>
              </a>
              <a
                href="/download"
                className="inline-flex min-w-[180px] items-center gap-3 border border-[var(--line-strong)] bg-surface px-5 py-[14px] text-ink transition-colors hover:border-accent"
                aria-label="Get Provenance on Google Play"
              >
                <GooglePlayIcon />
                <div>
                  <div className="font-mono text-[9px] tracking-[0.14em] uppercase text-ink-mute">
                    Get it on
                  </div>
                  <div className="font-mono text-[16px] font-[500] text-ink">
                    Google Play
                  </div>
                </div>
              </a>
            </div>

            <p className="mt-6 font-mono text-[10px] tracking-[0.16em] uppercase text-ink-mute">
              iOS 16+ · Android 11+ · ~24 MB
            </p>
          </div>

          <div className="flex flex-col items-center gap-4">
            <QrCode />
            <p className="font-mono text-[10px] tracking-[0.16em] uppercase text-ink-mute">
              Scan to install
            </p>
          </div>
        </div>
      </div>
    </section>
  )
}


function Footer() {
  return (
    <footer className="mt-20 border-t border-[var(--line)] bg-bg-2 pb-8 pt-16">
      <div className="mx-auto max-w-[1320px] px-[var(--gutter)]">
        <div className="mb-14 grid gap-12 sm:grid-cols-2 lg:grid-cols-[2fr_1fr_1fr]">
          {}
          <div>
            <Link
              to="/"
              className="mb-4 flex items-center gap-[10px] font-mono text-[13px] font-[600] tracking-[0.28em] uppercase text-ink"
              aria-label="Provenance — Home"
            >
              <span className="brand-mark" aria-hidden="true" />
              Provenance
            </Link>
            <p className="mt-2 max-w-[34ch] text-[13px] text-ink-soft">
              Digital product passports for fine watches. Issued, audited,
              and transferred under the authority of WatchCentral.
            </p>
            <p className="mt-6 font-mono text-[10px] tracking-[0.14em] uppercase text-ink-mute">
              WatchCentral · New York
            </p>
          </div>

          {}
          <nav aria-label="Product links">
            <h3 className="mb-4 font-mono text-[11px] tracking-[0.14em] uppercase text-ink-mute font-[500]">
              Product
            </h3>
            <ul className="flex flex-col gap-[10px] list-none p-0 m-0">
              <li>
                <a href="#product" className="text-[13px] text-ink-soft transition-colors hover:text-ink">
                  Overview
                </a>
              </li>
              <li>
                <a href="#how" className="text-[13px] text-ink-soft transition-colors hover:text-ink">
                  How it works
                </a>
              </li>
              <li>
                <a href="/download" className="text-[13px] text-ink-soft transition-colors hover:text-ink">
                  Download
                </a>
              </li>
            </ul>
          </nav>

          {}
          <nav aria-label="Company links">
            <h3 className="mb-4 font-mono text-[11px] tracking-[0.14em] uppercase text-ink-mute font-[500]">
              Company
            </h3>
            <ul className="flex flex-col gap-[10px] list-none p-0 m-0">
              <li>
                <a href="mailto:sales@watchcentral.com" className="text-[13px] text-ink-soft transition-colors hover:text-ink">
                  sales@watchcentral.com
                </a>
              </li>
              <li>
                <a href="mailto:support@watchcentral.com" className="text-[13px] text-ink-soft transition-colors hover:text-ink">
                  Support
                </a>
              </li>
              <li>
                <a href="/terms" className="text-[13px] text-ink-soft transition-colors hover:text-ink">
                  Terms
                </a>
              </li>
              <li>
                <a href="/privacy" className="text-[13px] text-ink-soft transition-colors hover:text-ink">
                  Privacy
                </a>
              </li>
            </ul>
          </nav>
        </div>

        {}
        <div className="flex flex-wrap items-center justify-between gap-4 border-t border-[var(--line)] pt-6 font-mono text-[10px] tracking-[0.12em] uppercase text-ink-mute">
          <span>© 2026 WatchCentral · All rights reserved</span>
          <span>Provenance v2.4.1</span>
        </div>
      </div>
    </footer>
  )
}


function HomePage() {
  return (
    <>
      <Nav />
      <main id="main-content">
        <HeroSection />
        <FeaturesSection />
        <HowSection />
        <IndustrySection />
        <DownloadSection />
      </main>
      <Footer />
    </>
  )
}
